import { useState } from 'react'
import { compressImage } from '@/lib/utils'
import type { BackgroundState } from '@/hooks/useAquarium'
import { BACKGROUND_PRESETS } from '@/data/backgrounds'
import stoneBackground from '@/assets/background/bg-stone.jpg'

// 预设背景映射
const PRESET_IMAGES: Record<string, string> = {
  stone: stoneBackground,
}

interface BackgroundUploadProps {
  currentBackground: BackgroundState
  onUpdate: (state: BackgroundState) => void
}

const MAX_FILE_SIZE = 10 * 1024 * 1024

export function BackgroundUpload({ currentBackground, onUpdate }: BackgroundUploadProps) {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setError('')

    if (file.size > MAX_FILE_SIZE) {
      setError('图片大小不能超过 10MB')
      return
    }

    if (!file.type.startsWith('image/')) {
      setError('请选择图片文件')
      return
    }

    setUploading(true)

    try {
      const base64 = await compressImage(file)
      onUpdate({ ...currentBackground, enabled: true, imageData: base64 })
    } catch {
      setError('图片处理失败，请重试')
    } finally {
      setUploading(false)
    }
  }

  const handleReset = () => {
    onUpdate({
      enabled: false,
      imageData: null,
      presetId: null,
      fitMode: 'cover',
      opacity: 0.8,
    })
  }

  const fitModeLabels = {
    cover: '覆盖',
    contain: '包含',
    fill: '填充',
  }

  const getCurrentImageSrc = () => {
    if (currentBackground.presetId && PRESET_IMAGES[currentBackground.presetId]) {
      return PRESET_IMAGES[currentBackground.presetId]
    }
    return currentBackground.imageData
  }

  const currentImageSrc = getCurrentImageSrc()

  const handleSelectPreset = (presetId: string) => {
    onUpdate({
      enabled: true,
      presetId,
      imageData: null,
      fitMode: currentBackground.fitMode,
      opacity: currentBackground.opacity,
    })
  }

  return (
    <div className="space-y-4">
      {/* 系统预设背景 */}
      <div>
        <label className="text-sm font-medium text-slate-600 mb-2 block">系统预设背景</label>
        <div className="grid grid-cols-4 gap-2">
          {BACKGROUND_PRESETS.map((preset) => (
            <button
              key={preset.id}
              onClick={() => handleSelectPreset(preset.id)}
              className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                currentBackground.enabled && currentBackground.presetId === preset.id
                  ? 'border-sky-500 ring-2 ring-sky-500 ring-offset-2'
                  : 'border-slate-200 hover:border-sky-300'
              }`}
            >
              <img
                src={PRESET_IMAGES[preset.id]}
                alt={preset.name}
                className="w-full h-full object-cover"
              />
              <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] text-center py-1">
                {preset.name}
              </span>
              {currentBackground.enabled && currentBackground.presetId === preset.id && (
                <div className="absolute top-1 right-1 w-4 h-4 bg-sky-500 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* 自定义上传 */}
      <div>
        <label className="text-sm font-medium text-slate-600 mb-2 block">自定义背景</label>
        <div className="relative w-full h-32 rounded-xl overflow-hidden bg-slate-200">
          {currentBackground.enabled && currentImageSrc ? (
            <img
              src={currentImageSrc}
              alt="背景预览"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <svg className="w-10 h-10 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-sm">点击上传背景图片</span>
            </div>
          )}
          <input
            type="file"
            accept="image/jpeg, image/png, image/webp"
            onChange={handleFileChange}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={uploading}
          />
          {uploading && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="px-3 py-2 rounded-lg bg-red-50 text-red-500 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium text-slate-600">适配模式</label>
          <div className="flex gap-2 mt-1">
            {(Object.keys(fitModeLabels) as Array<keyof typeof fitModeLabels>).map((mode) => (
              <button
                key={mode}
                onClick={() => onUpdate({ ...currentBackground, fitMode: mode })}
                className={`px-3 py-1.5 text-xs rounded-full transition-colors ${
                  currentBackground.fitMode === mode
                    ? 'bg-sky-500 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {fitModeLabels[mode]}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-slate-600">
            透明度: {Math.round(currentBackground.opacity * 100)}%
          </label>
          <input
            type="range"
            min="0.3"
            max="1"
            step="0.1"
            value={currentBackground.opacity}
            onChange={(e) => onUpdate({ ...currentBackground, opacity: parseFloat(e.target.value) })}
            className="w-full mt-1 accent-sky-500"
          />
        </div>

        {currentBackground.enabled && (
          <button
            onClick={handleReset}
            className="w-full px-4 py-2.5 text-sm text-red-500 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
          >
            恢复默认背景
          </button>
        )}
      </div>
    </div>
  )
}
